import { PlanetClass } from '@apeiron/planet'
import {
  ApeironInfinityDataGrid,
  ApeironInfinityDataGridRef,
  InfinityDatasourceResult,
  MayNull,
  NextImage,
  SkeletonBox,
  SkeletonTypography,
  useAccountContext
} from '@apeiron/temp'
import { ApeironTooltipSection } from '@apeiron/ui'
import { useWalletAddress } from '@apeiron/wallet'
import { Box, styled, useMediaQuery } from '@mui/material'
import InfoSVG from '@public/icons/icon-info.svg'
import BattleDemoRewardTips from '@src/components/battle/demo/leaderboard/BattleDemoRewardTips'
import {
  BattleDemoAddressCell,
  BattleDemoCellCenterContent,
  BattleDemoNameCell,
  BattleDemoRanking,
  BattleDemoRewardCell
} from '@src/components/battle/demo/leaderboard/BattleDemoSharedComponent'
import ApeironYouLabel from '@src/components/share/apeiron/ApeironYouLabel'
import Countdown from '@src/components/share/generic/Countdown'
import Dropdown from '@src/components/share/generic/Dropdown'
import {
  BattleDemoPlayMode,
  PLANET_CLASS_TOOLTIP_ID,
  REWARD_TOOLTIP_ID
} from '@src/constants/battleDemo'
import useGetBattleDemoMySoloPvELeaderboard from '@src/hooks/graphql/useGetBattleDemoMySoloPvELeaderboard'
import useGetBattleDemoPvESeasons from '@src/hooks/graphql/useGetBattleDemoPvESeasons'
import useGetBattleDemoSoloPvELeaderboard from '@src/hooks/graphql/useGetBattleDemoSoloPvELeaderboard'
import theme from '@src/styles/theme'
import {
  BattleDemoSeason,
  BattleDemoSoloPvELeaderboard,
  BattleDemoSoloPvERank
} from '@src/types/battleDemo'
import {
  BodyScrollEvent,
  CellClassRules,
  ColDef,
  GridApi,
  ICellRendererParams,
  IRowNode,
  PaginationChangedEvent,
  RowClassParams,
  RowClassRules
} from 'ag-grid-community'
import * as R from 'ramda'
import * as RA from 'ramda-adjunct'
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import useAsyncEffect from 'use-async-effect'

const Container = styled(Box)`
  align-items: center;
  display: flex;
  flex-direction: column;
  height: 100%;
  max-width: 1140px;
  padding: 0 20px;
  width: 100%;
`

const SeasonContainer = styled(Box)`
  display: flex;
  width: 100%;
  justify-content: flex-end;
`

const SeasonDropdown = styled(Dropdown)`
  margin-bottom: 20px;
`

const DataGridContainer = styled(Box)`
  align-items: center;
  display: flex;
  flex-direction: column;
  height: 100%;
  margin-bottom: 40px;
  min-height: 650px;
  width: 100%;
`

const CountdownContainer = styled(Box)`
  background-color: #222035;
  border-radius: 12px 12px 0 0;
  display: flex;
  flex-direction: row;
  justify-content: center;
  width: 780px;
  max-width: 100%;
  ${props => props.theme.breakpoints.up('res1024')} {
    gap: 40px;
    padding: 25px 0px 10px 0px;
  }
  ${props => props.theme.breakpoints.down('res1024')} {
    gap: 10px;
    padding: 20px 0px 5px 0px;
  }
`

const CountdownEndedContainer = styled(Box)`
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: 16px;
  height: 100%;
  justify-content: center;
  width: 100%;
  padding: 0px 20px;
`

const CountdownTitle = styled(SkeletonTypography)`
  font-size: 48px;
  font-weight: 400;
  line-height: 55px;
  min-width: 70%;
  text-align: center;
  text-transform: uppercase;
`

const CountdownSubtitle = styled(SkeletonBox)`
  border-radius: 10px;
  font-family: var(--font-Apeiron);
  font-size: 24px;
  font-weight: 400;
  line-height: 26px;
  min-width: 60%;
  text-align: center;
`

const DataGrid = styled(ApeironInfinityDataGrid)`
  flex: 1;
  overflow: hidden;
  .Custom-Grid {
    ${props => props.theme.breakpoints.up('res1024')} {
      border-radius: 12px 12px 0px 0px;
    }
    ${props => props.theme.breakpoints.down('res1024')} {
      border-radius: 0px;
      margin-bottom: 20px;
    }

    .ag-header {
      font-weight: 300;
      margin-bottom: -1px;
    }
    .ag-header-cell {
    }
    .ag-header-row {
      background-color: #222035;
    }
    .ag-cell {
      ${props => props.theme.breakpoints.up('res1024')} {
        padding: 0 8px;
      }
      ${props => props.theme.breakpoints.down('res1024')} {
      }
    }
    .custom-player-row {
      border-bottom: 4px solid #267ff8;
      border-top: 4px solid #267ff8;
      background-color: #0e0e12;
    }
    .custom-player-start-cell {
      border: unset;
      border-left: 4px solid #267ff8;
      background-color: #0e0e12;
    }
    .custom-player-end-cell {
      border: unset;
      border-right: 4px solid #267ff8;
      background-color: #0e0e12;
    }
  }
`

const TooltipHeaderContainer = styled(ApeironTooltipSection)`
  align-items: center;
  display: flex;
  gap: 10px;
  justify-content: center;
  width: 100%;
`

const YouLabel = styled(ApeironYouLabel)`
  position: fixed;
`

const ClassImage = styled(NextImage)`
  width: 30px;
  aspect-ratio: 1;
`

const InfoImage = styled(NextImage)`
  aspect-ratio: 1;
  width: 16px;
`

const RewardHeader: FC<RewardHeaderProps> = props => {
  const { t } = useTranslation()

  const { season } = props

  return (
    <TooltipHeaderContainer
      stay
      tooltip={
        <BattleDemoRewardTips rewardGroups={season?.rewardGroups || []} />
      }
      tooltipId={REWARD_TOOLTIP_ID}
    >
      {t('battle_demo.rewards')}
      <InfoImage src={InfoSVG} />
    </TooltipHeaderContainer>
  )
}

const BattleDemoClassCell: FC<ClassCellProps> = props => {
  const { record } = props

  if (R.isNil(record)) {
    return <></>
  }

  const planetClass = R.path(
    ['planet', 'planetClass'],
    record
  ) as MayNull<PlanetClass>

  const { name = '', type = '' } = planetClass || {}

  return (
    <ApeironTooltipSection
      tooltip={name}
      tooltipId={`${PLANET_CLASS_TOOLTIP_ID}_${type}`}
    >
      <ClassImage src={record.planet.icon} />
    </ApeironTooltipSection>
  )
}

const SoloPvELeaderboard: FC<Props> = props => {
  const { className } = props

  const mobile = useMediaQuery(theme.breakpoints.down('res1024'))

  const [leaderboard, setLeaderboard] =
    useState<MayNull<BattleDemoSoloPvELeaderboard>>(null)

  const [playerRowInView, setPlayerRowInView] = useState(true)

  const [selectedSeason, setSelectedSeason] =
    useState<MayNull<BattleDemoSeason>>(null)

  const ref = useRef<ApeironInfinityDataGridRef | null>(null)

  const { t } = useTranslation()

  const { account, isLoggedIn } = useAccountContext()

  const { compareAddress } = useWalletAddress()

  const { data: seasons, fetch: fetchSeasons } = useGetBattleDemoPvESeasons()

  const { fetch: fetchLeaderboard, loading: loadingLeaderboard } =
    useGetBattleDemoSoloPvELeaderboard()

  const { fetch: fetchMyLeaderboard, loading: loadingMyLeaderboard } =
    useGetBattleDemoMySoloPvELeaderboard()

  const fetch = isLoggedIn ? fetchMyLeaderboard : fetchLeaderboard

  const loading = isLoggedIn ? loadingMyLeaderboard : loadingLeaderboard

  const playerRank = useMemo(
    () => leaderboard?.record.playerRank,
    [leaderboard?.record.playerRank]
  )

  const endTime = useMemo(() => leaderboard?.summary.end || '0', [leaderboard])

  const isEnded = useMemo(
    () =>
      endTime === '0'
        ? true
        : new Date().getTime() > new Date(endTime).getTime(),
    [endTime]
  )

  const noReward = useMemo(
    () => R.isEmpty(selectedSeason?.rewardGroups || []),
    [selectedSeason?.rewardGroups]
  )

  const handleOnDropdownChange = useCallback(
    (value: string) => {
      setSelectedSeason(R.find(item => item.id === value, seasons))
    },
    [seasons]
  )

  const handleOnTimerEnd = useCallback(() => {
    if (ref.current) {
      ref.current.refreshData()
    }
  }, [])

  const handleCheckPlayerInRow = useCallback(
    (api: GridApi, rank: MayNull<BattleDemoSoloPvERank>) => {
      if (rank === null) return

      const { top: scrollTop, bottom: scrollBottom } =
        api.getVerticalPixelRange()

      const renderedNodes: IRowNode<BattleDemoSoloPvERank>[] = R.filter(
        (rowNode: IRowNode) => {
          const { rowTop, rowHeight } = rowNode

          return (
            Number(rowTop) + Number(rowHeight) >= scrollTop &&
            Number(rowTop) <= scrollBottom
          )
        },
        api.getRenderedNodes()
      )

      const searchedNode = R.find(
        (node: IRowNode<BattleDemoSoloPvERank>): boolean =>
          compareAddress(
            node.data?.user.walletAddress,
            rank?.user.walletAddress
          )
      )(renderedNodes)

      setPlayerRowInView(!R.isNil(searchedNode))
    },
    [compareAddress]
  )

  const handleOnBodyScroll = useCallback(
    (event: BodyScrollEvent<InfinityDatasourceResult>) => {
      handleCheckPlayerInRow(event.api, playerRank)
    },
    [handleCheckPlayerInRow, playerRank]
  )

  const handleOnPaginationChanged = useCallback(
    (event: PaginationChangedEvent<BattleDemoSoloPvERank>) => {
      handleCheckPlayerInRow(event.api, playerRank)
    },
    [handleCheckPlayerInRow, playerRank]
  )

  const handleOnFetch = useCallback(
    async (
      page: number,
      pageSize: number
    ): Promise<InfinityDatasourceResult> => {
      if (R.isNil(selectedSeason)) {
        return { data: [], total: 0 }
      }

      const { data } = await fetch({
        first: pageSize,
        season: selectedSeason.id,
        offset: (page - 1) * pageSize
      })

      setLeaderboard(data)

      const showClass = R.pathEq(
        BattleDemoPlayMode.Maxrooms,
        ['summary', 'playMode'],
        data
      )

      if (ref.current) {
        ref.current.setColumnVisible('class', showClass)
      }

      const ranks = R.pathOr([], ['record', 'ranks'], data)

      return {
        data: ranks,
        total: R.pathOr(0, ['record', 'pageInfo', 'total'], data)
      }
    },
    [fetch, selectedSeason]
  )

  useAsyncEffect(async (isActive: () => boolean) => {
    const result = await fetchSeasons({ isGuild: false })

    if (!isActive()) return

    const defaultSeason =
      R.find(R.propEq(true, 'active'), result) || R.head(result)

    setSelectedSeason(defaultSeason)
  }, [])

  useEffect(() => {
    if (ref.current && RA.isNotNilOrEmpty(selectedSeason?.id)) {
      ref.current.refreshData()
    }
  }, [isLoggedIn, account.walletAddress, selectedSeason?.id])

  const cellStartClassRules =
    useMemo((): CellClassRules<BattleDemoSoloPvERank> => {
      return {
        'custom-player-start-cell': (
          params: RowClassParams<BattleDemoSoloPvERank>
        ): boolean => {
          return (
            R.pathOr('no_rank', ['data', 'id'], params) ===
            R.propOr('no_player_rank', 'id', playerRank)
          )
        }
      }
    }, [playerRank])

  const cellEndClassRules =
    useMemo((): CellClassRules<BattleDemoSoloPvERank> => {
      return {
        'custom-player-end-cell': (
          params: RowClassParams<BattleDemoSoloPvERank>
        ): boolean => {
          return (
            R.pathOr('no_rank', ['data', 'id'], params) ===
            R.propOr('no_player_rank', 'id', playerRank)
          )
        }
      }
    }, [playerRank])

  const rowClassRules = useMemo((): RowClassRules<BattleDemoSoloPvERank> => {
    return {
      'custom-player-row': (
        params: RowClassParams<BattleDemoSoloPvERank>
      ): boolean => {
        return (
          R.pathOr('no_rank', ['data', 'id'], params) ===
          R.propOr('no_player_rank', 'id', playerRank)
        )
      }
    }
  }, [playerRank])

  const columns = useMemo((): ColDef<any, BattleDemoSoloPvERank>[] => {
    return [
      {
        cellClassRules: cellStartClassRules,
        field: 'start',
        headerName: '',
        width: mobile ? 0 : 40,
        pinned: 'left'
      },
      {
        cellClass: 'custom-ag-cell-center',
        field: 'rank',
        width: 50,
        headerName: '',
        headerClass: 'custom-ag-header-center',
        pinned: 'left',
        cellRenderer: (params: ICellRendererParams<BattleDemoSoloPvERank>) => {
          const isUser = compareAddress(
            params.data?.user.walletAddress,
            playerRank?.user.walletAddress
          )

          return (
            <>
              {isUser && <YouLabel />}
              <BattleDemoRanking record={params.data} />
            </>
          )
        }
      },
      {
        cellClass: 'custom-ag-cell-left',
        field: 'name',
        headerClass: 'custom-ag-header-left',
        headerName: t('battle_demo.leaderboard.grid_header.name'),
        width: mobile ? 150 : 220,
        cellRenderer: (params: ICellRendererParams<BattleDemoSoloPvERank>) => {
          return <BattleDemoNameCell record={params.data} />
        }
      },
      {
        cellClass: 'custom-ag-cell-center',
        field: 'class',
        flex: 1.5,
        minWidth: 150,
        headerClass: 'custom-ag-header-center',
        headerName: t('battle_demo.class'),
        cellRenderer: (params: ICellRendererParams<BattleDemoSoloPvERank>) => {
          return <BattleDemoClassCell record={params.data} />
        }
      },
      {
        cellClass: 'custom-ag-cell-center',
        field: 'address',
        minWidth: mobile ? 180 : 250,
        flex: 2,
        headerClass: 'custom-ag-header-center',
        headerName: t('battle_demo.leaderboard.grid_header.address'),
        cellRenderer: (params: ICellRendererParams<BattleDemoSoloPvERank>) => {
          return <BattleDemoAddressCell record={params.data} />
        }
      },
      {
        cellClass: 'custom-ag-cell-center',
        field: 'score',
        flex: 1,
        minWidth: 120,
        headerClass: 'custom-ag-header-center',
        headerName: t('battle_demo.score'),
        cellRenderer: (params: ICellRendererParams<BattleDemoSoloPvERank>) => {
          if (R.isNil(params.data)) {
            return ''
          }

          return (
            <BattleDemoCellCenterContent>
              {params.data?.score || '-'}
            </BattleDemoCellCenterContent>
          )
        }
      },
      {
        cellClass: 'custom-ag-cell-center',
        field: 'reward',
        headerClass: 'custom-ag-header-center',
        flex: 1,
        hide: noReward,
        minWidth: 300,
        headerComponent: () => {
          return <RewardHeader season={selectedSeason} />
        },
        cellRenderer: (params: ICellRendererParams<BattleDemoSoloPvERank>) => {
          return (
            <BattleDemoRewardCell
              record={params.data}
              season={selectedSeason}
            />
          )
        }
      },
      {
        cellClassRules: cellEndClassRules,
        field: 'end',
        headerName: '',
        pinned: 'right',
        width: 20
      }
    ]
  }, [
    cellEndClassRules,
    cellStartClassRules,
    compareAddress,
    mobile,
    noReward,
    playerRank?.user.walletAddress,
    selectedSeason,
    t
  ])

  return (
    <Container className={className}>
      <SeasonContainer>
        <SeasonDropdown
          items={seasons}
          value={
            R.isNotEmpty(seasons)
              ? selectedSeason?.id || R.head(seasons)?.id
              : ''
          }
          onDropdownChange={handleOnDropdownChange}
        />
      </SeasonContainer>
      <DataGridContainer>
        <CountdownContainer>
          {loading || isEnded ? (
            <CountdownEndedContainer>
              <CountdownTitle loading={loading}>
                {selectedSeason?.name}
              </CountdownTitle>
              <CountdownSubtitle loading={loading}>
                {selectedSeason?.name}
              </CountdownSubtitle>
            </CountdownEndedContainer>
          ) : (
            <Countdown targetDate={endTime} onComplete={handleOnTimerEnd} />
          )}
        </CountdownContainer>
        <DataGrid
          autoFetch={false}
          columnDefs={columns}
          headerHeight={mobile ? 60 : 80}
          gridRef={ref}
          pageSize={20}
          pinnedBottomRowData={
            playerRowInView || R.isNil(playerRank) ? undefined : [playerRank]
          }
          rowClassRules={rowClassRules}
          rowHeight={50}
          fetch={handleOnFetch}
          onBodyScroll={handleOnBodyScroll}
          onPaginationChanged={handleOnPaginationChanged}
        />
      </DataGridContainer>
    </Container>
  )
}

type RewardHeaderProps = {
  season: MayNull<BattleDemoSeason>
}

type ClassCellProps = {
  record: MayNull<BattleDemoSoloPvERank>
}

type Props = {
  className?: string
}

export default SoloPvELeaderboard
